import { useState, useEffect, useCallback, useMemo } from 'react';
import { groupsAPI } from '../../../../../services';
import { extractArray, getGroupId } from '../utils';

/**
 * Hook for managing group data fetching and state
 */
export function useGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchAllGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await groupsAPI.getAllGroups();
      const data = extractArray(result);

      // Augment with user counts if needed
      const augmented = await Promise.all(
        data.map(async (group) => {
          if (group.userCount !== undefined && group.userCount !== null) {
            return group;
          }
          if (Array.isArray(group.users) && group.users.length > 0) {
            return { ...group, userCount: group.users.length };
          }
          try {
            const usersResult = await groupsAPI.getUsersInGroup(getGroupId(group));
            const usersData = extractArray(usersResult);
            return { ...group, userCount: usersData.length, users: usersData };
          } catch {
            return { ...group, userCount: 0 };
          }
        })
      );

      setGroups(augmented);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
      setError(err.message || 'Failed to load group data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllGroups();
  }, [fetchAllGroups]);

  // Statistics
  const stats = useMemo(() => {
    const total = groups.length;
    const active = groups.filter((g) => g.isActive !== false).length;
    const totalUsers = groups.reduce((sum, g) => sum + (g.userCount || 0), 0);
    return { total, active, inactive: total - active, totalUsers };
  }, [groups]);

  // Delete group
  const deleteGroup = useCallback(async (groupId) => {
    const group = groups.find((g) => getGroupId(g) === groupId);
    if (group && groupsAPI.isDefaultGroup(group)) {
      throw new Error('Cannot delete the default group.');
    }
    setProcessing(true);
    try {
      await groupsAPI.deleteGroup(groupId);
      await fetchAllGroups();
    } finally {
      setProcessing(false);
    }
  }, [groups, fetchAllGroups]);

  // Get group details
  const getGroupDetails = useCallback(async (groupId) => {
    try {
      const result = await groupsAPI.getGroupDetails(groupId);
      let data = result;
      if (result.data?.data?.group) data = result.data.data.group;
      else if (result.data?.data) data = result.data.data;
      else if (result.data) data = result.data;
      return data;
    } catch (err) {
      console.error('Failed to fetch group details:', err);
      throw err;
    }
  }, []);

  return {
    groups,
    loading,
    error,
    processing,
    stats,
    fetchAllGroups,
    deleteGroup,
    getGroupDetails,
    isDefault: groupsAPI.isDefaultGroup,
  };
}

/**
 * Simple modal state hook
 */
export function useModal() {
  const [state, setState] = useState({
    create: false,
    edit: null,      // groupId
    users: null,     // { groupId, groupName }
    instruments: null, // { groupId, groupName }
    detail: null,    // groupId
  });

  const open = useCallback((key, value = true) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const close = useCallback((key) => {
    setState((prev) => ({ ...prev, [key]: key === 'edit' ? null : key === 'users' ? null : key === 'instruments' ? null : key === 'detail' ? null : false }));
  }, []);

  return { modalState: state, openModal: open, closeModal: close };
}
